// Trail: cards are laid out automatically and can be dragged individually.
// Drag the empty trail to move the view; link cards by dragging from a
// card's right handle to another card; remove links with the ✕ on hover.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Background, MarkerType, ReactFlow, ReactFlowProvider, useReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Minus, Plus } from "lucide-react";
import { toast } from "react-toastify";
import { useBuilder } from "../BuilderContext";
import { buildGraph } from "./graph";
import LinkEdge from "./LinkEdge";
import { nodeTypes } from "./nodes";

const edgeTypes = { link: LinkEdge };

const ZoomControls = () => {
  const { zoomIn, zoomOut } = useReactFlow();
  return (
    <div className="absolute bottom-4 right-4 z-10 flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <button type="button" onClick={() => zoomIn()} className="p-2 text-gray-600 hover:bg-gray-50">
        <Plus size={14} />
      </button>
      <button type="button" onClick={() => zoomOut()} className="border-t border-gray-200 p-2 text-gray-600 hover:bg-gray-50">
        <Minus size={14} />
      </button>
    </div>
  );
};

// Re-fits the view when cards are added/removed or the layout is reset.
const FitOnChange = ({ trigger }) => {
  const { fitView } = useReactFlow();
  useEffect(() => {
    const timer = setTimeout(() => fitView({ padding: 0.25, maxZoom: 1, duration: 200 }), 60);
    return () => clearTimeout(timer);
  }, [trigger, fitView]);
  return null;
};

const Trail = ({ fitVersion }) => {
  const { state, run, dispatch } = useBuilder();
  const { workflow } = state;
  const graph = useMemo(() => buildGraph(workflow), [workflow]);
  // Positions of cards the user has dragged; others keep the automatic layout.
  const [moved, setMoved] = useState({});
  const nodes = useMemo(() => graph.nodes.map((node) => (moved[node.id] ? { ...node, position: moved[node.id] } : node)), [graph, moved]);
  const onNodesChange = useCallback((changes) => {
    const positions = changes.filter((change) => change.type === "position" && change.position);
    if (positions.length) setMoved((prev) => ({ ...prev, ...Object.fromEntries(positions.map((change) => [change.id, change.position])) }));
  }, []);

  const edges = useMemo(
    () =>
      graph.edges.map((edge) => {
        const { tableId, stepId, outputId, color, customColor } = edge.data;
        return {
          ...edge,
          type: "link",
          markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
          data: {
            color,
            customColor,
            fromColor: graph.nodes.find((node) => node.id === edge.source)?.data.color,
            rows: Object.keys(state.files).length ? run?.tables[tableId]?.rowCount : undefined,
            onRemove: () => dispatch({ type: "disconnect", tableId, stepId, outputId }),
            onColor: (next) => dispatch({ type: "setLinkColor", tableId, stepId, outputId, color: next }),
          },
        };
      }),
    [graph, state.files, run, dispatch],
  );

  const onConnect = useCallback(
    ({ source, target }) => {
      const fromTable = graph.tableOfNode[source];
      if (!fromTable || source === target) return;
      if (workflow.outputs.some((o) => o.id === target)) {
        dispatch({ type: "connect", fromTable, outputId: target });
        return;
      }
      const group = graph.groups.find((g) => g.id === target);
      if (!group) return;
      const stepId = group.steps[0].step.id;
      const fromIndex = workflow.steps.findIndex((s) => s.id === fromTable);
      if (fromIndex >= group.steps[0].index) {
        toast.info("A step can only read from a source or an earlier step.");
        return;
      }
      dispatch({ type: "connect", fromTable, stepId });
    },
    [graph, workflow, dispatch],
  );

  return (
    <ReactFlow
      nodes={nodes}
      onNodesChange={onNodesChange}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onConnect={onConnect}
      nodesConnectable
      nodesDraggable
      elementsSelectable={false}
      panOnDrag
      selectionOnDrag={false}
      fitView
      fitViewOptions={{ padding: 0.25, maxZoom: 1 }}
      minZoom={0.2}
      proOptions={{ hideAttribution: true }}
    >
      <FitOnChange trigger={`${graph.nodes.length}:${fitVersion}`} />
      <div className="pointer-events-none absolute bottom-4 left-4 z-10 rounded-md bg-white/80 px-2 py-1 text-[11px] font-medium text-gray-500">
        Drag to move the trail
      </div>
      <Background gap={18} size={1.1} color="#cbd5e1" />
      <ZoomControls />
    </ReactFlow>
  );
};

const TrailView = ({ fitVersion }) => (
  <ReactFlowProvider>
    <Trail fitVersion={fitVersion} />
  </ReactFlowProvider>
);

export default TrailView;
